import { useEffect, useRef, useState } from 'react'

import { useRechargeForm } from '../../hooks/useRechargeForm'
import { useMercadoPago } from './useMercadoPago'

const MercadoPagoForm = () => {
  const { initialDataMP, isMercadoPagoActive, amount } = useRechargeForm()
  const formRef = useRef<HTMLFormElement>()
  const identificationTypeSelectRef = useRef<HTMLSelectElement>(null)
  const [isSelectValueSet, setIsSelectValueSet] = useState(false)

  const { isFormMounted } = useMercadoPago({
    isActive: isMercadoPagoActive && !!initialDataMP,
    amount
  })

  // Aguarda o select identificationType ser populado pelo Mercado Pago e define o valor
  useEffect(() => {
    if (!isFormMounted || !initialDataMP?.identificationType) return

    const select = identificationTypeSelectRef.current
    if (!select) return

    // Função para verificar se o select tem opções e definir o valor
    const setSelectValue = () => {
      // Verifica se o select tem opções (mais que apenas o placeholder)
      const hasOptions =
        select.options.length > 1 ||
        (select.options.length === 1 && select.options[0].value !== '')

      if (hasOptions) {
        // Verifica se o valor desejado existe nas opções
        const desiredValue = initialDataMP.identificationType
        const optionExists = Array.from(select.options).some(
          option => option.value === desiredValue
        )

        if (optionExists) {
          select.value = desiredValue

          // Dispara evento change para notificar o Mercado Pago
          const changeEvent = new Event('change', {
            bubbles: true,
            cancelable: true
          })
          select.dispatchEvent(changeEvent)
          setIsSelectValueSet(true)
          return true
        }
      }
      return false
    }

    // Tenta definir o valor imediatamente
    if (setSelectValue()) {
      return
    }

    // Se não conseguiu, aguarda o select ser populado
    // Usa MutationObserver para detectar quando opções são adicionadas
    const observer = new MutationObserver(() => {
      if (setSelectValue()) {
        observer.disconnect()
      }
    })

    observer.observe(select, {
      childList: true,
      subtree: true
    })

    // Timeout de fallback - tenta novamente após um tempo
    const timeout = setTimeout(() => {
      setSelectValue()
      observer.disconnect()
    }, 1000)

    return () => {
      observer.disconnect()
      clearTimeout(timeout)
    }
  }, [isFormMounted, initialDataMP?.identificationType])

  // Reset isSelectValueSet quando initialDataMP muda
  useEffect(() => {
    setIsSelectValueSet(false)
  }, [initialDataMP?.identificationType])

  // when form is ready with initial data and rendered, submit the form
  useEffect(() => {
    if (
      initialDataMP &&
      isMercadoPagoActive &&
      isFormMounted &&
      formRef.current
    ) {
      // Se não há identificationType ou já foi definido, pode submeter
      const canSubmit = !initialDataMP.identificationType || isSelectValueSet

      if (canSubmit) {
        // Aguarda um pouco para garantir que todos os campos foram processados
        const timeout = setTimeout(() => {
          // Dispara um evento submit que será capturado pelo handler do Mercado Pago
          // Não usar formRef.current.submit() diretamente, pois isso faz submit HTML nativo
          // que não é interceptado pelo Mercado Pago e causa redirecionamento
          const submitEvent = new Event('submit', {
            bubbles: true,
            cancelable: true
          })
          formRef.current?.dispatchEvent(submitEvent)
        }, 200) // Pequeno delay para garantir que tudo foi processado

        return () => clearTimeout(timeout)
      }
    }
  }, [initialDataMP, isMercadoPagoActive, isFormMounted, isSelectValueSet])

  return (
    !!initialDataMP && (
      <form id="form-checkout" ref={formRef} className="d-none">
        <select id="form-checkout__issuer" name="issuer" />
        <select id="form-checkout__installments" name="installments" />

        <input
          id="form-checkout__cardNumber"
          name="checkout__cardNumber"
          defaultValue={initialDataMP?.card_number}
        />
        <input
          id="form-checkout__expirationDate"
          name="expirationDate"
          maxLength={7}
          autoComplete="off"
          inputMode="numeric"
          placeholder="MM/YY"
          data-group=""
          defaultValue={initialDataMP?.card_expiration}
        />
        <input
          id="form-checkout__securityCode"
          name="securityCode"
          defaultValue={initialDataMP?.card_cvv}
        />

        <input
          type="text"
          id="form-checkout__cardholderName"
          defaultValue={initialDataMP?.card_name}
        />
        <select
          id="form-checkout__identificationType"
          ref={identificationTypeSelectRef}
          defaultValue={initialDataMP?.identificationType}
        />
        <input
          type="text"
          id="form-checkout__identificationNumber"
          defaultValue={initialDataMP?.identificationNumber}
        />
        <input
          type="email"
          id="form-checkout__cardholderEmail"
          defaultValue={initialDataMP?.email}
        />

        <button type="submit">Submit</button>
      </form>
    )
  )
}

export default MercadoPagoForm
